import React, { useState, useEffect, useRef } from 'react';
import {
  Input,
  Select,
  Button,
  Space,
  Tooltip,
  Switch,
  Typography,
  Modal,
  Checkbox,
  Spin,
  Empty,
  InputNumber,
  Row,
  Col,
  Rate,
  Slider as AntSlider,
} from 'antd';
import { toastSuccess, toastWarn } from '../Toast/ToastComponent';
import {
  PlusOutlined,
  CloseOutlined,
  CopyOutlined,
  ReloadOutlined,
  SearchOutlined,
  HolderOutlined,
  AppstoreAddOutlined,
} from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import { QuestionBank } from '../../Api/QuestionBankApi';
import axiosClient from '../../Api/axiosClient';
import style from '../../Styles/AddUser.module.css';
import { MdDeleteOutline } from 'react-icons/md';

const { Option } = Select;
const { Text, Title } = Typography;

const styles = {
  /* ── Category (List) card ── */
  categoryCard: {
    background: '#fff',
    borderRadius: 8,
    marginBottom: 20,
    border: '1px solid #d9e2ea',
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  categoryCardDragging: {
    background: '#eaf3fb',
    borderRadius: 8,
    marginBottom: 20,
    border: '2px dashed #134780',
    overflow: 'hidden',
    opacity: 0.6,
  },
  categoryHeader: {
    background: '#e8edf1',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #d4dce3',
  },
  categoryTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: '#2b4953',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  /* ── Section header bar (Questions / Options / Rows / Columns) ── */
  sectionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#dce8f0',
    padding: '8px 14px',
    borderRadius: 4,
    marginBottom: 10,
    fontWeight: 600,
    fontSize: 14,
    color: '#2b4953',
  },
  addDarkBtn: {
    width: 28,
    height: 28,
    borderRadius: 4,
    background: '#134780',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  /* ── Question row ── */
  questionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  questionInput: { flex: 1 },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 4,
    border: '1px solid #d9d9d9',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    color: '#999',
  },
  /* ── Option chips ── */
  optionsWrap: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 8,
    alignItems: 'center',
    marginBottom: 8,
  },
  optionChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
    border: '1px solid #d0dce3',
    borderRadius: 6,
    padding: '5px 10px',
    width: 'calc(25% - 6px)',
    minWidth: 0,
    boxSizing: 'border-box',
    justifyContent: 'flex-start',
    flexShrink: 0,
  },
  /* ── Matrix ── */
  matrixRowInput: {
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  columnRowInput: {
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  /* ── Free text ── */
  freeTextField: {
    background: '#f5f5f5',
    padding: 12,
    borderRadius: 6,
    marginTop: 8,
  },
  /* ── Required toggle ── */
  requiredToggleArea: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#555',
  },
  typeSelect: { width: 180 },
  removeCircleBtn: {
    width: 20,
    height: 20,
    borderRadius: 10,
    border: '1px solid #ccc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: '#f5f5f5',
    transition: 'all 0.3s',
    flexShrink: 0,
  },
};

const defaultOptions = [
  { title: '' },
  { title: '' },
  { title: '' },
  { title: '' },
];
const defaultMatrixRows = [
  { title: '' },
  { title: '' },
  { title: '' },
  { title: '' },
];
const defaultMatrixColumns = [
  { title: '' },
  { title: '' },
  { title: '' },
  { title: '' },
];

const createNewQuestion = () => ({
  localId: Date.now() + Math.random(),
  title: '',
  scaleType: 'radio',
  required: true,
  options: defaultOptions.map((o) => ({ ...o })),
  hasOtherOption: false,
  hasNoneOption: false,
  matrixRows: defaultMatrixRows.map((r) => ({ ...r })),
  matrixColumns: defaultMatrixColumns.map((c) => ({ ...c })),
  sliderMin: 0,
  sliderMax: 100,
});

const createNewCategory = () => ({
  localId: Date.now() + Math.random(),
  categoryId: null,
  categoryName: '',
  questions: [createNewQuestion()],
});

const Questions = ({
  categories,
  setCategories,
  categoryList = [],
  errors = {},
  setErrors,
  isPreview = false,
}) => {
  const [isQuestionBankVisible, setIsQuestionBankVisible] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [expandedOptions, setExpandedOptions] = useState({});
  const [searchText, setSearchText] = useState('');
  const [questionBankData, setQuestionBankData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(undefined);
  // Pagination for question bank
  const [qbOffset, setQbOffset] = useState(0);
  const [qbHasMore, setQbHasMore] = useState(false);
  const [qbLoadingMore, setQbLoadingMore] = useState(false);
  const QB_LIMIT = 50;

  // Category dropdown options
  const [surveyCategoryOptions, setSurveyCategoryOptions] = useState([]);
  const [surveyCategoryLoading, setSurveyCategoryLoading] = useState(false);

  // Drag & drop refs (categories only via native h5, questions via @hello-pangea/dnd)
  const dragCategoryIndex = useRef(null);
  const dragCategoryOverIndex = useRef(null);
  const [draggingCategoryId, setDraggingCategoryId] = useState(null);

  /* ── Fetch survey categories for dropdown ── */
  const fetchSurveyCategories = async () => {
    setSurveyCategoryLoading(true);
    try {
      const response = await axiosClient.post('/api/category/dropdown', {});
      if (response?.data?.success && Array.isArray(response?.data?.result)) {
        setSurveyCategoryOptions(response.data.result);
      } else {
        setSurveyCategoryOptions([]);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setSurveyCategoryOptions([]);
    } finally {
      setSurveyCategoryLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveyCategories();
  }, []);

  // Auto-scroll to first error field when parent sets errors
  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) return;
    const firstKey = Object.keys(errors)[0];
    // Map error key to element: question_{localId}, option_{localId}_{i}, matrixRow/Col, category_
    let elId = null;
    if (firstKey.startsWith('question_')) {
      elId = `question-${firstKey.replace('question_', '')}`;
    } else if (firstKey.startsWith('option_')) {
      const parts = firstKey.split('_');
      elId = `question-${parts[1]}`;
    } else if (
      firstKey.startsWith('matrixRow_') ||
      firstKey.startsWith('matrixCol_')
    ) {
      const parts = firstKey.split('_');
      elId = `question-${parts[1]}`;
    }
    if (elId) {
      const el = document.getElementById(elId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'box-shadow 0.3s';
        el.style.boxShadow = '0 0 0 2px rgba(255,77,79,0.4)';
        setTimeout(() => {
          el.style.boxShadow = '';
        }, 2000);
      }
    }
  }, [errors]);

  const fetchQuestionList = async (
    categoryId = 'all',
    offset = 0,
    append = false,
  ) => {
    console.log('CATEGORY ID:', categoryId, 'offset:', offset);
    if (offset === 0) setLoading(true);
    else setQbLoadingMore(true);

    const normalizeOptions = (optionField, qtype) => {
      if (!optionField) return [];

      if (typeof optionField === 'object' && !Array.isArray(optionField)) {
        if (qtype === 'matrix') {
          const rows = Array.isArray(optionField.rows) ? optionField.rows : [];
          const columns = Array.isArray(optionField.columns)
            ? optionField.columns
            : [];
          return { rows, columns };
        }
        return [];
      }

      if (Array.isArray(optionField)) {
        return optionField
          .map((item) => {
            if (qtype === 'slider' || qtype === 'sliders') return item;
            if (typeof item === 'string' || typeof item === 'number') return item;
            if (item && typeof item === 'object')
              return item.title ?? item.label ?? '';
            return '';
          })
          .filter((v) => v !== '' && v != null);
      }

      if (typeof optionField === 'string') {
        try {
          const parsed = JSON.parse(optionField);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => {
                if (qtype === 'slider' || qtype === 'sliders') return item;
                if (typeof item === 'string' || typeof item === 'number') return item;
                if (item && typeof item === 'object')
                  return item.title ?? item.label ?? '';
                return '';
              })
              .filter((v) => v !== '' && v != null);
          }

          if (parsed && typeof parsed === 'object') {
            if (qtype === 'matrix') {
              const rows = Array.isArray(parsed.rows) ? parsed.rows : [];
              const columns = Array.isArray(parsed.columns)
                ? parsed.columns
                : [];
              return { rows, columns };
            }
          }
        } catch (err) {
          // invalid JSON, fallthrough to string fallback
        }

        // if it's a plain comma-separated text, convert to array
        return optionField
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
      }

      return [];
    };

    try {
      const response =
        !categoryId || categoryId === 'all'
          ? await QuestionBank.getQuestionList({ limit: QB_LIMIT, offset })
          : await QuestionBank.getQuestionsByCategory(
              categoryId,
              QB_LIMIT,
              offset,
            );

      console.log('FULL API RESPONSE:', response.data);

      if (response?.data?.success) {
        const result = response.data?.result;
        const rawData = Array.isArray(result)
          ? result
          : Array.isArray(result?.question)
            ? result.question
            : Array.isArray(result?.rows)
              ? result.rows
              : [];

        console.log('API RAW:', rawData);

        // Determine if there are more records
        const totalCount = result?.count ?? result?.total ?? null;
        const newOffset = offset + rawData.length;
        if (totalCount !== null) {
          setQbHasMore(newOffset < totalCount);
        } else {
          setQbHasMore(rawData.length === QB_LIMIT);
        }
        setQbOffset(newOffset);

        const allowedTypes = [
          'radio',
          'checkbox',
          'matrix',
          'freeText',
          'text',
          'slider',
          'sliders',
          'rating',
        ];

        const transformed = rawData
          .map((q, i) => {
            const qtype = (q.question_type || '').toLowerCase();
            if (!allowedTypes.includes(qtype)) return null;

            const parsedOptions = normalizeOptions(q.option, qtype);
            const optionList =
              qtype === 'matrix' &&
              parsedOptions &&
              typeof parsedOptions === 'object' &&
              !Array.isArray(parsedOptions)
                ? []
                : parsedOptions;

            const matrixRows =
              qtype === 'matrix' &&
              parsedOptions &&
              typeof parsedOptions === 'object' &&
              !Array.isArray(parsedOptions)
                ? parsedOptions.rows
                : [];
            const matrixColumns =
              qtype === 'matrix' &&
              parsedOptions &&
              typeof parsedOptions === 'object' &&
              !Array.isArray(parsedOptions)
                ? parsedOptions.columns
                : [];

            return {
              id: q.id || i,
              question: q.question || q.title,
              type: qtype || 'radio',
              required:
                q.is_required === 1 ||
                q.is_required === true ||
                q.is_required === 'true' ||
                q.required === true,
              options: optionList,
              matrixRows,
              matrixColumns,
              categoryId: q.category_id,
              categoryName: q.category_name || q.categoryName || '',
            };
          })
          .filter(Boolean);

        console.log('TRANSFORMED:', transformed);

        if (append) {
          setQuestionBankData((prev) => {
            // Deduplicate by id
            const existingIds = new Set(prev.map((q) => q.id));
            const newItems = transformed.filter((q) => !existingIds.has(q.id));
            return [...prev, ...newItems];
          });
        } else {
          setQuestionBankData(transformed);
        }
      } else {
        if (!append) setQuestionBankData([]);
        setQbHasMore(false);
      }
    } catch (error) {
      console.log('ERROR:', error);
      if (!append) setQuestionBankData([]);
      setQbHasMore(false);
    } finally {
      setLoading(false);
      setQbLoadingMore(false);
    }
  };

  const refreshQuestionList = async () => {
    setRefreshing(true);
    setQbOffset(0);
    setQbHasMore(false);

    if (!selectedCategoryFilter || selectedCategoryFilter === 'all') {
      await fetchQuestionList('all', 0, false);
    } else {
      await fetchQuestionList(selectedCategoryFilter, 0, false);
    }

    setRefreshing(false);
  };

  const loadMoreQuestions = async () => {
    if (qbLoadingMore || !qbHasMore) return;
    const catId = selectedCategoryFilter || 'all';
    await fetchQuestionList(catId, qbOffset, true);
  };

  /* ── Category CRUD ── */
  const addCategory = () => {
    const newCategory = createNewCategory();
    setCategories((prev) => [...prev, newCategory]);
  };

  const updateCategoryName = (categoryLocalId, categoryId) => {
    const selectedCat = surveyCategoryOptions.find((c) => c.id === categoryId);
    setCategories((prev) =>
      prev.map((cat) =>
        cat.localId === categoryLocalId
          ? {
              ...cat,
              categoryId: categoryId,
              categoryName: selectedCat?.categoryName || '',
            }
          : cat,
      ),
    );
  };

  const deleteCategory = (categoryLocalId) => {
    if (categories.length === 1) {
      return toastWarn('At least one category is required');
    }
    setCategories((prev) =>
      prev.filter((cat) => cat.localId !== categoryLocalId),
    );
  };

  const duplicateCategory = (categoryLocalId) => {
    const target = categories.find((cat) => cat.localId === categoryLocalId);
    if (!target) return;

    const copy = {
      ...target,
      localId: Date.now() + Math.random(),
      categoryName: target.categoryName ? `${target.categoryName} (Copy)` : '',
      categoryId: target.categoryId,
      questions: target.questions.map((q) => ({
        ...q,
        localId: Date.now() + Math.random(),
        options: q.options.map((o) => ({ ...o })),
        matrixRows: q.matrixRows.map((r) => ({ ...r })),
        matrixColumns: q.matrixColumns.map((c) => ({ ...c })),
      })),
    };

    setCategories((prev) => [...prev, copy]);
  };

  /* ── Question CRUD within category ── */
  const addQuestion = (categoryLocalId) => {
    const newQuestion = createNewQuestion();
    setCategories((prev) =>
      prev.map((cat) =>
        cat.localId === categoryLocalId
          ? { ...cat, questions: [...cat.questions, newQuestion] }
          : cat,
      ),
    );
    setTimeout(() => {
      document
        .getElementById(`question-${newQuestion.localId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const addQuestionBelow = (categoryLocalId, questionLocalId) => {
    const newQuestion = createNewQuestion();
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        const idx = cat.questions.findIndex(
          (q) => q.localId === questionLocalId,
        );
        if (idx === -1)
          return { ...cat, questions: [...cat.questions, newQuestion] };
        const updatedQuestions = [...cat.questions];
        updatedQuestions.splice(idx + 1, 0, newQuestion);
        return { ...cat, questions: updatedQuestions };
      }),
    );
    setTimeout(() => {
      document
        .getElementById(`question-${newQuestion.localId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const deleteQuestion = (categoryLocalId, questionLocalId) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        if (cat.questions.length === 1) {
          toastWarn('At least one question required per section');
          return cat;
        }
        return {
          ...cat,
          questions: cat.questions.filter((q) => q.localId !== questionLocalId),
        };
      }),
    );
  };

  const duplicateQuestion = (categoryLocalId, questionLocalId) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        const idx = cat.questions.findIndex(
          (q) => q.localId === questionLocalId,
        );
        if (idx === -1) return cat;
        const target = cat.questions[idx];

        const copy = {
          ...target,
          localId: Date.now() + Math.random(),
          options: target.options.map((o) => ({ ...o })),
          matrixRows: target.matrixRows.map((r) => ({ ...r })),
          matrixColumns: target.matrixColumns.map((c) => ({ ...c })),
        };

        const updatedQuestions = [...cat.questions];
        updatedQuestions.splice(idx + 1, 0, copy);

        return {
          ...cat,
          questions: updatedQuestions,
        };
      }),
    );
  };

  const updateQuestionField = (
    categoryLocalId,
    questionLocalId,
    field,
    value,
  ) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        return {
          ...cat,
          questions: cat.questions.map((q) =>
            q.localId === questionLocalId ? { ...q, [field]: value } : q,
          ),
        };
      }),
    );
  };

  /* ── Options ── */
  const addOption = (categoryLocalId, questionLocalId) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        return {
          ...cat,
          questions: cat.questions.map((q) =>
            q.localId === questionLocalId
              ? { ...q, options: [...q.options, { title: '' }] }
              : q,
          ),
        };
      }),
    );
  };

  const updateOption = (categoryLocalId, questionLocalId, idx, value) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        return {
          ...cat,
          questions: cat.questions.map((q) => {
            if (q.localId !== questionLocalId) return q;
            const updated = [...q.options];
            updated[idx] = { ...updated[idx], title: value };
            return { ...q, options: updated };
          }),
        };
      }),
    );
  };

  const deleteOption = (categoryLocalId, questionLocalId, idx) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        return {
          ...cat,
          questions: cat.questions.map((q) => {
            if (q.localId !== questionLocalId) return q;
            if (q.options.length === 1) {
              toastWarn('At least one option required');
              return q;
            }
            return { ...q, options: q.options.filter((_, i) => i !== idx) };
          }),
        };
      }),
    );
  };

  /* ── Matrix Rows ── */
  const addMatrixRow = (categoryLocalId, questionLocalId) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        return {
          ...cat,
          questions: cat.questions.map((q) =>
            q.localId === questionLocalId
              ? { ...q, matrixRows: [...q.matrixRows, { title: '' }] }
              : q,
          ),
        };
      }),
    );
  };

  const updateMatrixRow = (categoryLocalId, questionLocalId, idx, value) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        return {
          ...cat,
          questions: cat.questions.map((q) => {
            if (q.localId !== questionLocalId) return q;
            const rows = [...q.matrixRows];
            rows[idx] = { ...rows[idx], title: value };
            return { ...q, matrixRows: rows };
          }),
        };
      }),
    );
  };

  const deleteMatrixRow = (categoryLocalId, questionLocalId, idx) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        return {
          ...cat,
          questions: cat.questions.map((q) => {
            if (q.localId !== questionLocalId) return q;
            if (q.matrixRows.length === 1) {
              toastWarn('At least one row required');
              return q;
            }
            return {
              ...q,
              matrixRows: q.matrixRows.filter((_, i) => i !== idx),
            };
          }),
        };
      }),
    );
    // toastSuccess('Row deleted');
  };

  /* ── Matrix Columns ── */
  const addMatrixColumn = (categoryLocalId, questionLocalId) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        return {
          ...cat,
          questions: cat.questions.map((q) =>
            q.localId === questionLocalId
              ? { ...q, matrixColumns: [...q.matrixColumns, { title: '' }] }
              : q,
          ),
        };
      }),
    );
  };

  const updateMatrixColumn = (categoryLocalId, questionLocalId, idx, value) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        return {
          ...cat,
          questions: cat.questions.map((q) => {
            if (q.localId !== questionLocalId) return q;
            const cols = [...q.matrixColumns];
            cols[idx] = { ...cols[idx], title: value };
            return { ...q, matrixColumns: cols };
          }),
        };
      }),
    );
  };

  const deleteMatrixColumn = (categoryLocalId, questionLocalId, idx) => {
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.localId !== categoryLocalId) return cat;
        return {
          ...cat,
          questions: cat.questions.map((q) => {
            if (q.localId !== questionLocalId) return q;
            if (q.matrixColumns.length === 1) {
              toastWarn('At least one column required');
              return q;
            }
            return {
              ...q,
              matrixColumns: q.matrixColumns.filter((_, i) => i !== idx),
            };
          }),
        };
      }),
    );
    // toastSuccess('Column deleted');
  };

  /* ── Drag & Drop for Categories ── */
  const handleCategoryDragStart = (e, idx, localId) => {
    dragCategoryIndex.current = idx;
    setDraggingCategoryId(localId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCategoryDragEnter = (e, idx) => {
    e.preventDefault();
    dragCategoryOverIndex.current = idx;
  };

  const handleCategoryDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleCategoryDrop = (e, idx) => {
    e.preventDefault();
    const from = dragCategoryIndex.current;
    const to = dragCategoryOverIndex.current ?? idx;

    if (from === null || from === to) {
      setDraggingCategoryId(null);
      return;
    }

    setCategories((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(from, 1);
      updated.splice(to, 0, moved);
      return updated;
    });

    dragCategoryIndex.current = null;
    dragCategoryOverIndex.current = null;
    setDraggingCategoryId(null);
  };

  const handleCategoryDragEnd = () => {
    dragCategoryIndex.current = null;
    dragCategoryOverIndex.current = null;
    setDraggingCategoryId(null);
  };

  const handleMasterDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination, type } = result;

    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    if (type === 'CATEGORY') {
      setCategories((prev) => {
        const updated = Array.from(prev);
        const [moved] = updated.splice(source.index, 1);
        updated.splice(destination.index, 0, moved);
        return updated;
      });
    } else {
      // type === 'QUESTION' — droppableId format is "questions-{localId}"
      const sourceCatLocalId  = Number(source.droppableId.replace('questions-', ''));
      const destCatLocalId    = Number(destination.droppableId.replace('questions-', ''));

      if (sourceCatLocalId === destCatLocalId) {
        // Reorder within same category
        setCategories((prev) =>
          prev.map((cat) => {
            if (cat.localId !== sourceCatLocalId) return cat;
            const updatedQs = Array.from(cat.questions);
            const [movedQ] = updatedQs.splice(source.index, 1);
            updatedQs.splice(destination.index, 0, movedQ);
            return { ...cat, questions: updatedQs };
          }),
        );
      } else {
        // Move between categories
        setCategories((prev) => {
          const sourceCat = prev.find((c) => c.localId === sourceCatLocalId);
          if (!sourceCat) return prev;
          const movedQ = sourceCat.questions[source.index];
          return prev.map((cat) => {
            if (cat.localId === sourceCatLocalId) {
              const updatedQs = Array.from(cat.questions);
              updatedQs.splice(source.index, 1);
              return { ...cat, questions: updatedQs };
            }
            if (cat.localId === destCatLocalId) {
              const updatedQs = Array.from(cat.questions);
              updatedQs.splice(destination.index, 0, movedQ);
              return { ...cat, questions: updatedQs };
            }
            return cat;
          });
        });
      }
    }
  };


  const showQuestionBank = () => {
    setIsQuestionBankVisible(true);
    setSelectedQuestions([]);
    setSearchText('');
    setQbOffset(0);
    setQbHasMore(false);

    // Default to show all questions from the bank
    setSelectedCategoryFilter(undefined);
    fetchQuestionList('all', 0, false);
  };

  const handleQBCancel = () => {
    setIsQuestionBankVisible(false);
    setSelectedQuestions([]);
  };

  const handleQBOk = () => {
    if (selectedQuestions.length === 0) {
      toastWarn('Please select at least one question');
      return;
    }

    // Group questions by their category
    const questionsByCategory = {};
    selectedQuestions.forEach((q) => {
      const categoryName = q.categoryName;
      if (!questionsByCategory[categoryName]) {
        questionsByCategory[categoryName] = [];
      }
      questionsByCategory[categoryName].push(q);
    });

    // Create new categories with questions
    const newCategories = Object.entries(questionsByCategory).map(
      ([categoryName, questions]) => ({
        localId: Date.now() + Math.random() + Math.random(),
        categoryId: questions[0]?.categoryId || null,
        categoryName: categoryName,
        questions: questions.map((q) => {
          const isMatrix = q.type === 'matrix';
          let mappedScaleType = q.type || 'radio';
          if (mappedScaleType === 'checkbox') mappedScaleType = 'multiChoice';
          if (mappedScaleType === 'slider') mappedScaleType = 'sliders';
          if (mappedScaleType === 'text') mappedScaleType = 'freeText';

          return {
            ...createNewQuestion(),
            title: q.question,
            scaleType: mappedScaleType,
            required: q.required || false,
            options:
              q.options?.map((o) => {
                if (mappedScaleType === 'sliders') {
                  return {
                    title: typeof o === 'string' ? o : o.title || '',
                    min: o.min !== undefined ? o.min : 0,
                    max: o.max !== undefined ? o.max : 100,
                  };
                }
                return {
                  title: (typeof o === 'string' || typeof o === 'number') ? String(o) : o.title || '',
                };
              }) || defaultOptions.map((o) => ({ ...o })),
            ...(isMatrix
              ? {
                  matrixRows:
                    Array.isArray(q.matrixRows) && q.matrixRows.length > 0
                      ? q.matrixRows.map((r) => ({
                          title: typeof r === 'string' ? r : r.title || '',
                        }))
                      : defaultMatrixRows.map((r) => ({ ...r })),
                  matrixColumns:
                    Array.isArray(q.matrixColumns) && q.matrixColumns.length > 0
                      ? q.matrixColumns.map((c) => ({
                          title: typeof c === 'string' ? c : c.title || '',
                        }))
                      : defaultMatrixColumns.map((c) => ({ ...c })),
                }
              : {}),
          };
        }),
      }),
    );

    setCategories((prev) => [...prev, ...newCategories]);
    toastSuccess(
      `${selectedQuestions.length} question(s) added in ${newCategories.length} section(s)`,
    );
    setIsQuestionBankVisible(false);
    setSelectedQuestions([]);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategoryFilter(value);
    setQbOffset(0);
    setQbHasMore(false);
    setQuestionBankData([]);
    if (!value || value === 'all') {
      fetchQuestionList('all', 0, false);
    } else {
      fetchQuestionList(value, 0, false);
    }
    // Do NOT reset selectedQuestions when switching category so users can
    // accumulate selections across categories before adding to survey.
  };

  const toggleQBSelection = (question) => {
    setSelectedQuestions((prev) => {
      const isSelected = prev.some((q) => q.id === question.id);
      if (isSelected) {
        return prev.filter((q) => q.id !== question.id);
      } else {
        return [...prev, question];
      }
    });
  };

  const filteredQB = questionBankData.filter((q) => {
    const matchesSearch = (q.question || '')
      .toLowerCase()
      .includes((searchText || '').toLowerCase());
    return matchesSearch;
  });

  const allSelected =
    filteredQB.length > 0 &&
    filteredQB.every((q) => selectedQuestions.some((s) => s.id === q.id));
  const indeterminate =
    selectedQuestions.some((s) => filteredQB.some((q) => q.id === s.id)) &&
    !allSelected;

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 10,
        border: '1px solid #dde3e8',
        padding: '20px 24px',
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      }}
    >
      <style>{`
        .hover-action-btn, .hover-delete-btn, .hover-remove-icon, .hover-add-btn { transition: all 0.3s ease; }
        .hover-action-btn:hover { background-color: #e6f0fb !important; }
        .hover-delete-btn:hover { background-color: #ffe6e6 !important; border-color: #ff4d4f !important; }
        .hover-remove-icon:hover { background-color: #d9d9d9 !important; }
        .hover-add-btn:hover { 
          background-color: #2f80ed !important; 
          box-shadow: 0 4px 12px rgba(47, 128, 237, 0.35);
        }
        .error-bg-hover:hover { background-color: #ffe0e0 !important; }
        .hover-copy-icon:hover {
          color: #2f80ed !important;
        }
      `}</style>
      {/* ── Page Header ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 20,
        }}
      >
        <div className={style['title-style']}>Survey Questions</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            icon={<AppstoreAddOutlined />}
            onClick={showQuestionBank}
            disabled={isPreview}
          >
            Add From Question Bank
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={addCategory}
            disabled={isPreview}
            style={{ height: 32 }}
          ></Button>
        </div>
      </div>

      {/* ── List Cards ── */}
      <DragDropContext onDragEnd={handleMasterDragEnd}>
        <Droppable droppableId="board" type="CATEGORY">
          {(boardProvided) => (
            <div ref={boardProvided.innerRef} {...boardProvided.droppableProps}>
              {categories.map((category, catIdx) => {
                const isDraggingCategory =
                  draggingCategoryId === category.localId;

                return (
                  <Draggable
                    key={`cat-${category.localId}`}
                    draggableId={`cat-${category.localId}`}
                    index={catIdx}
                    isDragDisabled={isPreview}
                  >
                    {(catProvided, catSnapshot) => (
                      <div
                        ref={catProvided.innerRef}
                        {...catProvided.draggableProps}
                        style={{
                          ...styles.categoryCard,
                          ...catProvided.draggableProps.style,
                          ...(catSnapshot.isDragging
                            ? styles.categoryCardDragging
                            : {}),
                        }}
                      >
                        {/* ── List Header ── */}
                        <div style={styles.categoryHeader}>
                          <div
                            {...catProvided.dragHandleProps}
                            style={{
                              ...styles.categoryTitle,
                              cursor: isPreview ? 'default' : 'grab',
                            }}
                          >
                            <HolderOutlined
                              style={{ color: '#9ba8b5', fontSize: 13 }}
                            />
                            <span>
                              {category.categoryName || `List ${catIdx + 1}`}
                            </span>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                            }}
                          >
                       <button
  onClick={() => !isPreview && duplicateCategory(category.localId)}
  title="Duplicate List"
  disabled={isPreview}
  style={{
    border: 'none',
    background: 'transparent',
    cursor: isPreview ? 'not-allowed' : 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
  
>
  <CopyOutlined
    className={!isPreview ? "hover-copy-icon" : ""}
    style={{
      color: isPreview ? '#ccc' : '#134780', // same blue
      fontSize: 14,
      transition: '0.2s',
    }}
  />
</button>
                            <button
                              onClick={() =>
                                !isPreview && deleteCategory(category.localId)
                              }
                              title="Delete List"
                              disabled={isPreview}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                cursor: isPreview ? 'not-allowed' : 'pointer',
                                padding: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              className="hover-delete-btn"
                            >
                              <MdDeleteOutline
                                size={18}
                                style={{
                                  color: isPreview ? '#ccc' : '#f15658', // Ant Design red
                                  transition: '0.2s',
                                }}
                              />
                            </button>
                          </div>
                        </div>

                        {/* ── Category row ── */}
                        <div
                          style={{
                            padding: '12px 16px 0 16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              color: '#e03232',
                              fontWeight: 600,
                              fontSize: 13,
                            }}
                          >
                            *
                          </span>
                          <span
                            style={{
                              fontSize: 13,
                              color: '#2b4953',
                              fontWeight: 500,
                            }}
                          >
                            Category:
                          </span>
                          <div
                            className={
                              errors[`category_${category.localId}`]
                                ? 'error-bg-hover'
                                : ''
                            }
                          >
                            <Select
                              value={category.categoryId || undefined}
                              onChange={(val) => {
                                updateCategoryName(category.localId, val);
                                if (
                                  errors[`category_${category.localId}`] &&
                                  setErrors
                                ) {
                                  setErrors((prev) => {
                                    const errs = { ...prev };
                                    delete errs[`category_${category.localId}`];
                                    return errs;
                                  });
                                }
                              }}
                              style={{ width: 200 }}
                              placeholder="Select Category"
                              loading={surveyCategoryLoading}
                              disabled={isPreview}
                              allowClear
                              size="middle"
                              status={
                                errors[`category_${category.localId}`]
                                  ? 'error'
                                  : ''
                              }
                            >
                              {surveyCategoryOptions.map((cat) => (
                                <Option key={cat.id} value={cat.id}>
                                  {cat.categoryName}
                                </Option>
                              ))}
                            </Select>
                          </div>
                        </div>

                        {/* ── Questions + Options per question ── */}
                        <div style={{ padding: '12px 16px 16px 16px' }}>
                          <Droppable
                            droppableId={`questions-${category.localId}`}
                            type="QUESTION"
                          >
                            {(droppableProvided) => (
                              <div
                                ref={droppableProvided.innerRef}
                                {...droppableProvided.droppableProps}
                              >
                                {category.questions.map((q, qIdx) => {
                                  const isMatrix = q.scaleType === 'matrix';
                                  const isFreeText =
                                    q.scaleType === 'freeText' ||
                                    q.scaleType === 'text';
                                  const isCheckbox =
                                    q.scaleType === 'multiChoice' ||
                                    q.scaleType === 'checkbox';
                                  const isRating = q.scaleType === 'rating';
                                  const isSlider = q.scaleType === 'sliders';

                                  return (
                                    <Draggable
                                      key={String(q.localId)}
                                      draggableId={String(q.localId)}
                                      index={qIdx}
                                      isDragDisabled={isPreview}
                                    >
                                      {(
                                        draggableProvided,
                                        draggableSnapshot,
                                      ) => (
                                        <div
                                          id={`question-${q.localId}`}
                                          ref={draggableProvided.innerRef}
                                          {...draggableProvided.draggableProps}
                                          style={{
                                            ...draggableProvided.draggableProps
                                              .style,
                                            opacity:
                                              draggableSnapshot.isDragging
                                                ? 0.85
                                                : 1,
                                            marginBottom: 10,
                                            border: '1px solid #dde3e8',
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            background:
                                              draggableSnapshot.isDragging
                                                ? '#eaf3fb'
                                                : '#fff',
                                            boxShadow:
                                              draggableSnapshot.isDragging
                                                ? '0 4px 16px rgba(0,0,0,0.15)'
                                                : 'none',
                                          }}
                                        >
                                          {/* ── Question Card Header ── */}
                                          <div
                                            style={{
                                              background: '#f0f4f7',
                                              padding: '8px 14px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              borderBottom: '1px solid #dde3e8',
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                              }}
                                            >
                                              <span
                                                {...draggableProvided.dragHandleProps}
                                              >
                                                <HolderOutlined
                                                  style={{
                                                    cursor: 'grab',
                                                    color: '#9ba8b5',
                                                    fontSize: 12,
                                                  }}
                                                />
                                              </span>
                                              <span
                                                style={{
                                                  fontWeight: 600,
                                                  fontSize: 13,
                                                  color: '#2b4953',
                                                }}
                                              >
                                                Q{qIdx + 1}
                                              </span>
                                            </div>
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12,
                                              }}
                                            >
                                              <div
                                                style={
                                                  styles.requiredToggleArea
                                                }
                                              >
                                                <Switch
                                                  checked={q.required}
                                                  onChange={(val) =>
                                                    updateQuestionField(
                                                      category.localId,
                                                      q.localId,
                                                      'required',
                                                      val,
                                                    )
                                                  }
                                                  disabled={isPreview}
                                                />
                                                <span>Required</span>
                                              </div>
                                              <button
                                                title="Duplicate Question"
                                                onClick={() =>
                                                  !isPreview &&
                                                  duplicateQuestion(
                                                    category.localId,
                                                    q.localId,
                                                  )
                                                }
                                                disabled={isPreview}
                                                style={{
                                                  border: 'none',
                                                  background: 'transparent',
                                                  cursor: isPreview
                                                    ? 'not-allowed'
                                                    : 'pointer',
                                                  padding: 4,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                }}
                                              >
                                                <CopyOutlined
                                                  className={!isPreview ? "hover-copy-icon" : ""}
                                                  style={{
                                                    color: isPreview
                                                      ? '#ccc'
                                                      : '#134780',
                                                    fontSize: 14,
                                                    transition: '0.2s',
                                                  }}
                                                />
                                              </button>
                                              <button
                                                title="Delete Question"
                                                onClick={() =>
                                                  !isPreview &&
                                                  deleteQuestion(
                                                    category.localId,
                                                    q.localId,
                                                  )
                                                }
                                                disabled={isPreview}
                                                style={{
                                                  border: 'none',
                                                  background: 'transparent',
                                                  cursor: isPreview
                                                    ? 'not-allowed'
                                                    : 'pointer',
                                                  padding: 4,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                }}
                                                className="hover-delete-btn"
                                              >
                                                <MdDeleteOutline
                                                  size={18}
                                                  style={{
                                                    color: isPreview
                                                      ? '#ccc'
                                                      : '#ff4d4f',
                                                    transition: '0.2s',
                                                  }}
                                                />
                                              </button>
                                            </div>
                                          </div>

                                          {/* ── Question Input + Type Selector on same row ── */}
                                          <div
                                            style={{
                                              padding: '12px 14px 0 14px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 8,
                                            }}
                                          >
                                            <Input
                                              placeholder="Enter your Question"
                                              status={
                                                errors[`question_${q.localId}`]
                                                  ? 'error'
                                                  : ''
                                              }
                                              value={q.title}
                                              onChange={(e) => {
                                                updateQuestionField(
                                                  category.localId,
                                                  q.localId,
                                                  'title',
                                                  e.target.value,
                                                );
                                                if (
                                                  errors[
                                                    `question_${q.localId}`
                                                  ] &&
                                                  setErrors
                                                ) {
                                                  setErrors((prev) => {
                                                    const errs = { ...prev };
                                                    delete errs[
                                                      `question_${q.localId}`
                                                    ];
                                                    return errs;
                                                  });
                                                }
                                              }}
                                              style={{ flex: 1 }}
                                              disabled={isPreview}
                                            />
                                            <Select
                                              value={
                                                q.scaleType === 'text'
                                                  ? 'freeText'
                                                  : q.scaleType || 'radio'
                                              }
                                           onChange={(val) => {
  let updatedOptions = q.options;

  // ✅ Reset when switching to rating
  if (val === 'rating') {
    updatedOptions = [{ title: '5' }]; // fresh default
  }

  // ✅ Reset when switching to slider
  if (val === 'sliders') {
    updatedOptions = [{ min: 0, max: 100 }];
  }

  updateQuestionField(category.localId, q.localId, 'scaleType', val);
  updateQuestionField(category.localId, q.localId, 'options', updatedOptions);
}}
                                              style={{ width: 140 }}
                                              disabled={isPreview}
                                              size="middle"
                                            >
                                              <Option value="radio">
                                                Radio
                                              </Option>
                                              <Option value="multiChoice">
                                                Check Box
                                              </Option>
                                              <Option value="freeText">
                                                Free Text
                                              </Option>
                                              <Option value="matrix">
                                                Matrix
                                              </Option>
                                              <Option value="sliders">
                                                Slider
                                              </Option>
                                              <Option value="rating">Rating</Option>
                                            </Select>
                                          </div>

                                          {/* ── Question Body ── */}
                                          <div
                                            style={{
                                              padding: '10px 14px 14px 14px',
                                            }}
                                          >
                                            {/* ── Matrix Rows ── */}
                                            {isMatrix && (
                                              <>
                                                <div
                                                  style={{
                                                    ...styles.sectionBar,
                                                    marginTop: 4,
                                                  }}
                                                >
                                                  <span>Questions</span>
                                                  <button
                                                    className={!isPreview ? "hover-add-btn" : ""}
                                                    style={styles.addDarkBtn}
                                                    onClick={() =>
                                                      addMatrixRow(
                                                        category.localId,
                                                        q.localId,
                                                      )
                                                    }
                                                  >
                                                    <PlusOutlined
                                                      style={{
                                                        color: '#fff',
                                                        fontSize: 13,
                                                      }}
                                                    />
                                                  </button>
                                                </div>
                                                {q.matrixRows.map((row, ri) => (
                                                  <div
                                                    key={ri}
                                                    style={
                                                      styles.matrixRowInput
                                                    }
                                                  >
                                                    <Input
                                                      placeholder="Enter your Question"
                                                      status={
                                                        errors[
                                                          `matrixRow_${q.localId}_${ri}`
                                                        ]
                                                          ? 'error'
                                                          : ''
                                                      }
                                                      value={row.title}
                                                      onChange={(e) => {
                                                        updateMatrixRow(
                                                          category.localId,
                                                          q.localId,
                                                          ri,
                                                          e.target.value,
                                                        );
                                                        if (
                                                          errors[
                                                            `matrixRow_${q.localId}_${ri}`
                                                          ] &&
                                                          setErrors
                                                        ) {
                                                          setErrors((prev) => {
                                                            const errs = {
                                                              ...prev,
                                                            };
                                                            delete errs[
                                                              `matrixRow_${q.localId}_${ri}`
                                                            ];
                                                            return errs;
                                                          });
                                                        }
                                                      }}
                                                      style={{ flex: 1 }}
                                                      disabled={isPreview}
                                                    />
                                                    {!isPreview && (
                                                      <div
                                                        style={
                                                          styles.removeCircleBtn
                                                        }
                                                        className="hover-remove-icon"
                                                        onClick={() =>
                                                          deleteMatrixRow(
                                                            category.localId,
                                                            q.localId,
                                                            ri,
                                                          )
                                                        }
                                                      >
                                                        <CloseOutlined
                                                          style={{
                                                            fontSize: 9,
                                                            color: '#9aa0a6',
                                                          }}
                                                        />
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                                <div
                                                  style={{
                                                    ...styles.sectionBar,
                                                    marginTop: 10,
                                                  }}
                                                >
                                                  <span>Options </span>
                                                  <button
                                                    className={!isPreview ? "hover-add-btn" : ""}
                                                    style={styles.addDarkBtn}
                                                    onClick={() =>
                                                      addMatrixColumn(
                                                        category.localId,
                                                        q.localId,
                                                      )
                                                    }
                                                  >
                                                    <PlusOutlined
                                                      style={{
                                                        color: '#fff',
                                                        fontSize: 13,
                                                      }}
                                                    />
                                                  </button>
                                                </div>
                                                {q.matrixColumns.map(
                                                  (col, ci) => (
                                                    <div
                                                      key={ci}
                                                      style={
                                                        styles.columnRowInput
                                                      }
                                                    >
                                                      <Input
                                                        placeholder="Enter your Option"
                                                        status={
                                                          errors[
                                                            `matrixCol_${q.localId}_${ci}`
                                                          ]
                                                            ? 'error'
                                                            : ''
                                                        }
                                                        value={col.title}
                                                        onChange={(e) => {
                                                          updateMatrixColumn(
                                                            category.localId,
                                                            q.localId,
                                                            ci,
                                                            e.target.value,
                                                          );
                                                          if (
                                                            errors[
                                                              `matrixCol_${q.localId}_${ci}`
                                                            ] &&
                                                            setErrors
                                                          ) {
                                                            setErrors(
                                                              (prev) => {
                                                                const errs = {
                                                                  ...prev,
                                                                };
                                                                delete errs[
                                                                  `matrixCol_${q.localId}_${ci}`
                                                                ];
                                                                return errs;
                                                              },
                                                            );
                                                          }
                                                        }}
                                                        style={{ flex: 1 }}
                                                        disabled={isPreview}
                                                      />
                                                      {!isPreview && (
                                                        <div
                                                          style={
                                                            styles.removeCircleBtn
                                                          }
                                                          className="hover-remove-icon"
                                                          onClick={() =>
                                                            deleteMatrixColumn(
                                                              category.localId,
                                                              q.localId,
                                                              ci,
                                                            )
                                                          }
                                                        >
                                                          <CloseOutlined
                                                            style={{
                                                              fontSize: 9,
                                                              color: '#9aa0a6',
                                                            }}
                                                          />
                                                        </div>
                                                      )}
                                                    </div>
                                                  ),
                                                )}
                                              </>
                                            )}

                                            {/* ── Rating ── */}
                                            {/* ── Rating ── */}
                                            {isRating && (
                                              <div style={{ marginTop: 12 }}>
                                                <div style={{ border: '1px solid #d0dce3', borderRadius: '6px', overflow: 'hidden' }}>
                                                  <div style={styles.sectionBar}>
                                                    <span style={{ fontSize: '13px' }}>Rating Scale</span>
                                                  </div>
                                                  <div style={{ padding: '16px' }}>
                                                    <Row gutter={16} align="middle">
                                                      <Col span={8}>
                                                        <Text style={{ fontSize: '12px', color: '#666' }}>Number of Stars</Text>
                                                        <InputNumber
                                                          min={1}
                                                          max={10}
                                                       value={q.scaleType === 'rating' ? Number(q.options?.[0]?.title || 5) : 5}
                                                          onChange={(val) => updateOption(category.localId, q.localId, 0, String(val))}
                                                          style={{ width: '100%', marginTop: 4 }}
                                                          disabled={isPreview}
                                                        />
                                                      </Col>
                                                      <Col span={16}>
                                                        <Text style={{ fontSize: '12px', color: '#666' }}>Preview</Text>
                                                        <div style={{ marginTop: 8 }}>
                                                          <Rate value={Number(q.options?.[0]?.title) || 0} count={Number(q.options?.[0]?.title) || 5} style={{ fontSize: 18, color: '#fadb14' }} />
                                                        </div>
                                                      </Col>
                                                    </Row>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                            {/* ── Slider ── */}
                                            {isSlider && (
                                              <div style={{ marginTop: 12 }}>
                                                <div style={{ border: '1px solid #d0dce3', borderRadius: '6px', overflow: 'hidden' }}>
                                                  <div style={styles.sectionBar}>
                                                    <span style={{ fontSize: '13px' }}>Slider Range</span>
                                                  </div>
                                                  <div style={{ padding: '16px' }}>
                                                    <Row gutter={16}>
                                                      <Col span={12}>
                                                        <Text style={{ fontSize: '12px', color: '#666' }}>Min Value</Text>
                                                        <InputNumber
                                                          min={0}
                                                          max={(q.options?.[0]?.max || 100) - 1}
                                                          value={q.options?.[0]?.min ?? 0}
                                                          onChange={(val) => {
                                                            const updatedList = Array.from(categories);
                                                            const cIdx = updatedList.findIndex(c => c.localId === category.localId);
                                                            const qIdx = updatedList[cIdx].questions.findIndex(qu => qu.localId === q.localId);
                                                            const opts = [...updatedList[cIdx].questions[qIdx].options];
                                                            opts[0] = { ...opts[0], min: val, max: opts[0]?.max ?? 100 };
                                                            updatedList[cIdx].questions[qIdx].options = opts;
                                                            setCategories(updatedList);
                                                          }}
                                                          style={{ width: '100%', marginTop: 4 }}
                                                          disabled={isPreview}
                                                        />
                                                      </Col>
                                                      <Col span={12}>
                                                        <Text style={{ fontSize: '12px', color: '#666' }}>Max Value</Text>
                                                        <InputNumber
                                                          min={(q.options?.[0]?.min ?? 0) + 1}
                                                          max={1000}
                                                          value={q.options?.[0]?.max}
                                                          onChange={(val) => {
                                                            const updatedList = Array.from(categories);
                                                            const cIdx = updatedList.findIndex(c => c.localId === category.localId);
                                                            const qIdx = updatedList[cIdx].questions.findIndex(qu => qu.localId === q.localId);
                                                            const opts = [...updatedList[cIdx].questions[qIdx].options];
                                                            opts[0] = { ...opts[0], min: opts[0]?.min ?? 0, max: val };
                                                            updatedList[cIdx].questions[qIdx].options = opts;
                                                            setCategories(updatedList);
                                                          }}
                                                          style={{ width: '100%', marginTop: 4 }}
                                                          disabled={isPreview}
                                                        />
                                                      </Col>
                                                    </Row>
                                                    <div style={{ marginTop: 16 }}>
                                                      <div style={{
                                                          flex: 1, position: 'relative', height: 6, background: '#e0e6ea', borderRadius: 3
                                                        }}>
                                                            <div style={{ position: 'absolute', left: 0, top: 0, width: '50%', height: '100%', background: isPreview ? '#a0b4c8' : '#134780', borderRadius: 3 }} />
                                                            <div style={{ position: 'absolute', left: 'calc(50% - 10px)', top: -7, width: 20, height: 20, borderRadius: '50%', background: isPreview ? '#a0b4c8' : '#134780', boxShadow: isPreview ? 'none' : '0 2px 6px rgba(19,71,128,0.35)', border: '2px solid #fff', cursor: isPreview ? 'not-allowed' : 'default' }} />
                                                      </div>
                                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                                                        <Text style={{ fontSize: '11px', color: '#999' }}>{q.options?.[0]?.min ?? 0}</Text>
                                                        <Text style={{ fontSize: '11px', color: '#999' }}>{q.options?.[0]?.max ?? 100}</Text>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                            {/* ── Options ── */}
                                            {!isMatrix &&
                                              !isFreeText &&
                                              !isRating &&
                                              !isSlider && (
                                                <div style={{ marginTop: 4 }}>
                                                  <div
                                                    style={styles.sectionBar}
                                                  >
                                                    <span>Options</span>
                                                    {!isPreview && (
                                                      <button
                                                        className="hover-add-btn"
                                                        style={
                                                          styles.addDarkBtn
                                                        }
                                                        onClick={() =>
                                                          addOption(
                                                            category.localId,
                                                            q.localId,
                                                          )
                                                        }
                                                      >
                                                        <PlusOutlined
                                                          style={{
                                                            color: '#fff',
                                                            fontSize: 13,
                                                          }}
                                                        />
                                                      </button>
                                                    )}
                                                  </div>
                                                  {/* Render options in rows of max 4, each chip is 25% width */}
                                                  {Array.from({
                                                    length: Math.ceil(
                                                      q.options.length / 4,
                                                    ),
                                                  }).map((_, rowIdx) => (
                                                    <div
                                                      key={rowIdx}
                                                      style={{
                                                        display: 'flex',
                                                        flexWrap: 'nowrap',
                                                        gap: 8,
                                                        alignItems: 'stretch',
                                                        marginBottom: 8,
                                                      }}
                                                    >
                                                      {q.options
                                                        .slice(
                                                          rowIdx * 4,
                                                          rowIdx * 4 + 4,
                                                        )
                                                        .map((opt, relIdx) => {
                                                          const oi =
                                                            rowIdx * 4 + relIdx;
                                                          return (
                                                            <div
                                                              key={oi}
                                                              className={
                                                                errors[
                                                                  `option_${q.localId}_${oi}`
                                                                ]
                                                                  ? 'survey-error-element'
                                                                  : ''
                                                              }
                                                              style={{
                                                                ...styles.optionChip,
                                                                border: errors[
                                                                  `option_${q.localId}_${oi}`
                                                                ]
                                                                  ? '1px solid red'
                                                                  : '1px solid #d0dce3',
                                                                background:
                                                                  errors[
                                                                    `option_${q.localId}_${oi}`
                                                                  ]
                                                                    ? '#fff1f0'
                                                                    : isPreview
                                                                      ? '#f5f7fa'
                                                                      : '#fff',
                                                                cursor:
                                                                  isPreview
                                                                    ? 'not-allowed'
                                                                    : 'default',
                                                                pointerEvents:
                                                                  isPreview
                                                                    ? 'none'
                                                                    : 'auto',
                                                                opacity:
                                                                  isPreview
                                                                    ? 0.75
                                                                    : 1,
                                                              }}
                                                            >
                                                              {isCheckbox ? (
                                                                <div
                                                                  style={{
                                                                    width: 14,
                                                                    height: 14,
                                                                    borderRadius: 3,
                                                                    border:
                                                                      '1.5px solid #134780',
                                                                    flexShrink: 0,
                                                                    background:
                                                                      '#fff',
                                                                  }}
                                                                />
                                                              ) : (
                                                                <div
                                                                  style={{
                                                                    width: 14,
                                                                    height: 14,
                                                                    borderRadius: 7,
                                                                    border:
                                                                      '2px solid #134780',
                                                                    flexShrink: 0,
                                                                    background:
                                                                      '#fff',
                                                                  }}
                                                                />
                                                              )}
                                                              <input
                                                                value={
                                                                  opt.title
                                                                }
                                                                onChange={(
                                                                  e,
                                                                ) => {
                                                                  updateOption(
                                                                    category.localId,
                                                                    q.localId,
                                                                    oi,
                                                                    e.target
                                                                      .value,
                                                                  );
                                                                  if (
                                                                    errors[
                                                                      `option_${q.localId}_${oi}`
                                                                    ] &&
                                                                    setErrors
                                                                  ) {
                                                                    setErrors(
                                                                      (
                                                                        prev,
                                                                      ) => {
                                                                        const errs =
                                                                          {
                                                                            ...prev,
                                                                          };
                                                                        delete errs[
                                                                          `option_${q.localId}_${oi}`
                                                                        ];
                                                                        return errs;
                                                                      },
                                                                    );
                                                                  }
                                                                }}
                                                                placeholder="Enter your Choice"
                                                                readOnly={
                                                                  isPreview
                                                                }
                                                                style={{
                                                                  border:
                                                                    'none',
                                                                  outline:
                                                                    'none',
                                                                  background:
                                                                    'transparent',
                                                                  fontSize: 13,
                                                                  color:
                                                                    isPreview
                                                                      ? '#8c99a4'
                                                                      : '#2b4953',
                                                                  flex: 1,
                                                                  minWidth: 0,
                                                                  width: '100%',
                                                                  cursor:
                                                                    isPreview
                                                                      ? 'not-allowed'
                                                                      : 'text',
                                                                }}
                                                                disabled={
                                                                  isPreview
                                                                }
                                                              />
                                                              {!isPreview && (
                                                                <div
                                                                  onClick={() =>
                                                                    deleteOption(
                                                                      category.localId,
                                                                      q.localId,
                                                                      oi,
                                                                    )
                                                                  }
                                                                  className="hover-remove-icon"
                                                                  style={{
                                                                    width: 18,
                                                                    height: 18,
                                                                    borderRadius: 9,
                                                                    border:
                                                                      '1px solid #ccc',
                                                                    display:
                                                                      'flex',
                                                                    alignItems:
                                                                      'center',
                                                                    justifyContent:
                                                                      'center',
                                                                    cursor:
                                                                      'pointer',
                                                                    flexShrink: 0,
                                                                    marginLeft: 2,
                                                                    background:
                                                                      '#f5f5f5',
                                                                  }}
                                                                >
                                                                  <CloseOutlined
                                                                    style={{
                                                                      fontSize: 9,
                                                                      color:
                                                                        '#888',
                                                                    }}
                                                                  />
                                                                </div>
                                                              )}
                                                            </div>
                                                          );
                                                        })}
                                                    </div>
                                                  ))}
                                                </div>
                                              )}

                                            {/* ── Free Text ── */}
                                            {isFreeText && (
                                              <div
                                                style={{
                                                  marginTop: 4,
                                                  background: '#f5f5f5',
                                                  padding: 12,
                                                  borderRadius: 6,
                                                }}
                                              >
                                                <Input.TextArea
                                                  placeholder="Enter your response here..."
                                                  rows={3}
                                                  disabled
                                                />
                                              </div>
                                            )}


                                          </div>
                                          {/* end question body */}
                                        </div>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {droppableProvided.placeholder}
                              </div>
                            )}
                          </Droppable>

                          {/* ── Add Question button below last card ── */}
                          {!isPreview && (
                            <Button
                              icon={<PlusOutlined />}
                              onClick={() => addQuestion(category.localId)}
                              style={{
                                marginTop: 6,
                                height: 32,
                                paddingInline: 14,
                                fontSize: 13,
                              }}
                            >
                              Add Question
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {boardProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* ── Bottom action button ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        <Button
          icon={<PlusOutlined />}
          onClick={addCategory}
          disabled={isPreview}
          type="primary"
          style={{ height: 32, paddingInline: 14, fontSize: 13 }}
        >
          Add list
        </Button>
      </div>

      {/* Global Question Bank Modal */}
      <Modal
        title={null}
        open={isQuestionBankVisible}
        onCancel={handleQBCancel}
        width={800}
        footer={null}
        bodyStyle={{ padding: 0 }}
        closable={false}
        centered
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {/* Header with Title and Refresh */}
          <div
            style={{
              background: '#f8f9fa',
              padding: '12px 16px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Title
              level={5}
              style={{ margin: 0, color: '#2b4953', fontSize: 16 }}
            >
              Question Bank
            </Title>
            <Tooltip title="Cancel">
              <CloseOutlined
                style={{ color: '#999', cursor: 'pointer', fontSize: 18 }}
                onClick={handleQBCancel}
                className="hover-remove-icon"
              />
            </Tooltip>
          </div>

          {/* Search and Filter Section */}
          <div
            style={{
              padding: '12px 16px',
              background: '#fff',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <Select
              placeholder="Category"
              value={selectedCategoryFilter}
              onChange={handleCategoryChange}
              style={{ width: 250, height: 32 }}
              allowClear
            >
              {surveyCategoryOptions.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.categoryName}
                </Option>
              ))}
            </Select>
            <Input
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: 1, height: 32, fontSize: 13 }}
              prefix={
                <SearchOutlined style={{ color: '#aaa', fontSize: 14 }} />
              }
              allowClear
            />
          </div>

          {/* Select All Bar */}
          <div
            style={{
              background: '#fff',
              padding: '8px 16px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <Checkbox
              checked={allSelected}
              indeterminate={indeterminate}
              onChange={(e) => {
                if (e.target.checked) {
                  // merge filteredQB into selectedQuestions without duplicates
                  setSelectedQuestions((prev) => {
                    const merged = [...prev];
                    filteredQB.forEach((q) => {
                      if (!merged.some((s) => s.id === q.id)) merged.push(q);
                    });
                    return merged;
                  });
                } else {
                  // deselect only the visible filtered questions
                  setSelectedQuestions((prev) =>
                    prev.filter((s) => !filteredQB.some((q) => q.id === s.id)),
                  );
                }
              }}
              style={{ fontSize: 12 }}
            >
              <span style={{ fontSize: 12, fontWeight: 500 }}>Select All</span>
            </Checkbox>
            {selectedQuestions.length > 0 && (
              <span
                style={{
                  background: '#134780',
                  color: '#fff',
                  padding: '2px 6px',
                  borderRadius: 10,
                  fontSize: 10,
                }}
              >
                {selectedQuestions.length} selected
              </span>
            )}
          </div>

          {/* Questions List - Scrollable */}
          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 250,
                background: '#fff',
              }}
            >
              {/* <Spin size="small" /> */}
            </div>
          ) : (
            <div
              style={{
                padding: '12px 16px',
                maxHeight: 300,
                overflowY: 'auto',
                background: '#fff',
              }}
            >
              {filteredQB.length === 0 ? (
                <Empty
                  description="No questions found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                filteredQB.map((q) => {
                  const isSelected = selectedQuestions.some(
                    (s) => s.id === q.id,
                  );
                  const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

                  return (
                    <div
                      key={q.id}
                      className="qb-question-card"
                      style={{
                        border: isSelected
                          ? '2px solid #134780'
                          : '1px solid #e9ecef',
                        borderRadius: 6,
                        padding: 12,
                        marginBottom: 8,
                        background: isSelected ? '#f0f7ff' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        position: 'relative',
                      }}
                      onClick={() => toggleQBSelection(q)}
                    >
                      {/* Selection Indicator */}
                      {isSelected && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 16,
                            height: 16,
                            borderRadius: 8,
                            background: '#134780',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: 10,
                          }}
                        >
                          ✓
                        </div>
                      )}

                      {/* Question Content */}
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: '#2b4953',
                            marginBottom: 6,
                            paddingRight: 20,
                            lineHeight: 1.4,
                          }}
                        >
                          {q.question}
                        </div>

                        {/* Options */}
                        {q.type !== 'freeText' &&
                          q.options?.filter(
                            (opt) =>
                              opt &&
                              (typeof opt === 'string'
                                ? opt.trim()
                                : opt.title?.trim()),
                          ).length > 0 && (
                            <div style={{ marginTop: 6 }}>
                              {q.options
                                .filter(
                                  (opt) =>
                                    opt &&
                                    (typeof opt === 'string'
                                      ? opt.trim()
                                      : opt.title?.trim()),
                                )
                                .slice(0, expandedOptions[q.id] ? undefined : 2)
                                .map((choice, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '1px 0',
                                      fontSize: 11,
                                      color: '#666',
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: 8,
                                        background: '#e9ecef',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: 6,
                                        color: '#495057',
                                        fontWeight: 500,
                                        fontSize: 9,
                                      }}
                                    >
                                      {letters[i]}
                                    </span>
                                    <span style={{ fontSize: 11 }}>
                                      {typeof choice === 'object'
                                        ? choice.title
                                        : choice}
                                    </span>
                                  </div>
                                ))}
                              {q.options.filter(
                                (opt) =>
                                  opt &&
                                  (typeof opt === 'string'
                                    ? opt.trim()
                                    : opt.title?.trim()),
                              ).length > 2 && (
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: '#0056b3',
                                    marginTop: 2,
                                    cursor: 'pointer',
                                    display: 'inline-block'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedOptions(prev => ({...prev, [q.id]: !prev[q.id]}));
                                  }}
                                >
                                  {expandedOptions[q.id] ? 'Show Less' : `+ ${q.options.filter(
                                    (opt) =>
                                      opt &&
                                      (typeof opt === 'string'
                                        ? opt.trim()
                                        : opt.title?.trim()),
                                  ).length - 2} more options`}
                                </div>
                              )}
                            </div>
                          )}

                        {/* Tags */}
                        <div
                          style={{
                            marginTop: 8,
                            display: 'flex',
                            gap: 4,
                            flexWrap: 'wrap',
                          }}
                        >
                          {q.categoryName && (
                            <span
                              style={{
                                background: '#f0f0f0',
                                padding: '1px 6px',
                                borderRadius: 10,
                                fontSize: 10,
                                color: '#666',
                              }}
                            >
                              {q.categoryName}
                            </span>
                          )}
                          <span
                            style={{
                              background: '#e2eef5',
                              padding: '1px 6px',
                              borderRadius: 10,
                              fontSize: 10,
                              color: '#134780',
                              textTransform: 'capitalize',
                            }}
                          >
                            {q.type}
                          </span>
                          {q.required && (
                            <span
                              style={{
                                background: '#fff1f0',
                                padding: '1px 6px',
                                borderRadius: 10,
                                fontSize: 10,
                                color: '#f5222d',
                              }}
                            >
                              Required
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Show More button */}
          {qbHasMore && filteredQB.length > 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '8px 16px',
                background: '#fff',
                borderTop: '1px solid #e9ecef',
              }}
            >
              <Button
                size="small"
                onClick={loadMoreQuestions}
                loading={qbLoadingMore}
                style={{ fontSize: 12 }}
              >
                Show More
              </Button>
            </div>
          )}

          {/* Footer with Actions */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '12px 16px',
              background: '#f8f9fa',
              borderTop: '1px solid #e9ecef',
            }}
          >
            <Space size={8}>
              <Button
                size="medium"
                onClick={handleQBCancel}
                style={{ fontSize: 12 }}
              >
                Cancel
              </Button>
              <Button
                size="medium"
                type="primary"
                onClick={handleQBOk}
                disabled={selectedQuestions.length === 0}
                style={{ background: '#134780', fontSize: 12, color: 'white' }}
              >
                Add Selected ({selectedQuestions.length})
              </Button>
            </Space>
          </div>
        </div>
      </Modal>
      <style>{`
        .hover-copy-icon:hover {
          color: #2f80ed !important;
          transform: scale(1.2);
        }
        .hover-add-btn {
          transition: all 0.3s;
        }
        .hover-add-btn:hover {
          background: #2f80ed !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }
        .qb-question-card:hover {
          background-color: #f0f7ff !important;
        }
      `}</style>
    </div>
  );
};

export default Questions;
